import { useAnimatedHeight } from '@insify/react-hooks';
import { TrackedButton, TrackedExpandableContext } from '@objectiv/tracker-react';
import clsx from 'clsx';
import { forwardRef } from 'react';
import { ChevronDown } from 'react-feather';

import { HelpCircle } from '../../icons';
import { textMd, textMdSemibold, textSm, textXxsSemibold } from '../../typography';
import { Checkbox } from '../Checkbox';

import styles from './ActivityCard.module.css';

import type { CheckboxProps } from '../Checkbox';
import type { TrackingProps } from '../types';
import type { ComponentPropsWithRef, ReactNode } from 'react';

type ItemProps = CheckboxProps & {
  header: string;
  isSuggested?: boolean;
  description?: string;
  onDetailsClick?: (name: string) => void;
  className?: string;
};

export const ActivityCardItem = ({
  className,
  header,
  isSuggested,
  description,
  onDetailsClick,
  suggestedTagText,
  ...itemProps
}: ItemProps & { suggestedTagText?: string }) => {
  return (
    <li className={clsx(styles.item, className)}>
      <label className={styles.label}>
        <Checkbox className={styles.checkbox} {...itemProps} />
        <span className={styles.itemTextContent}>
          <span>
            {/* Header and suggestion tag are separated by space */}
            <span className={clsx(textMd, styles.itemHeader)} data-testid={`label-${itemProps.id}`}>
              {header}{' '}
              {isSuggested && (
                <span className={clsx(textXxsSemibold, styles.itemSuggestedTag)}>{suggestedTagText}</span>
              )}
            </span>
          </span>
          {description && <span className={clsx(textSm, styles.itemDescription)}>{description}</span>}
        </span>
        {onDetailsClick && (
          <HelpCircle
            className={styles.details}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onDetailsClick(itemProps.name);
            }}
          />
        )}
      </label>
    </li>
  );
};

export type ActivityCardProps = TrackingProps & {
  id: string;
  icon: ReactNode;
  header?: string;
  expandedByDefault?: boolean;
  suggestedTagText?: string;
  items: ItemProps[];
  className?: string;
};

type InnerActivityCardProps = Omit<ActivityCardProps, 'trackingId' | 'expandedByDefault'> &
  Omit<ReturnType<typeof useAnimatedHeight>, 'ref'>;

const InnerActivityCard = forwardRef<HTMLDivElement, InnerActivityCardProps>(
  ({ className, id, toggle, isOpen, icon, header: cardHeader, items, suggestedTagText }, ref) => {
    return (
      <div className={clsx(styles.container, className)}>
        <TrackedButton
          className={styles.button}
          type='button'
          objectiv={{ id: 'trigger' }}
          aria-controls={`${id}-details`}
          onClick={toggle}
          aria-expanded={isOpen}
        >
          <div className={styles.icon}>{icon}</div>
          {cardHeader && <span className={clsx(textMdSemibold, styles.header)}>{cardHeader}</span>}
          <ChevronDown className={clsx(styles.chevron, isOpen && styles.expanded)} />
        </TrackedButton>
        <div id={`${id}-details`} ref={ref}>
          <ul className={styles.list}>
            {items.map((itemProps, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <ActivityCardItem key={index} suggestedTagText={suggestedTagText} {...itemProps} />
            ))}
          </ul>
        </div>
      </div>
    );
  },
);

export const ActivityCard = ({ expandedByDefault, trackingId, ...props }: ActivityCardProps): JSX.Element => {
  const {
    ref: animatedHeightRef,
    toggle,
    isOpen,
  } = useAnimatedHeight<HTMLDivElement>({
    initialState: expandedByDefault ? 'open' : 'closed',
  });

  return (
    <TrackedExpandableContext<ComponentPropsWithRef<typeof InnerActivityCard>>
      ref={animatedHeightRef}
      objectiv={{
        id: trackingId,
        isVisible: isOpen,
        Component: InnerActivityCard,
      }}
      toggle={toggle}
      isOpen={isOpen}
      {...props}
    />
  );
};
